import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import { Callout, FormDialog } from '@nyce/admin-kit/console';
import { Button, Chip, Switch, TextField } from '@nyce/admin-kit/ui';
import { ErrorBox, Loading, Page, TableScroller, useAsync } from '../../components/common';
import { SCOPES } from '../../api';
import { useHubLogin } from '../../session';
import { authClientFor, identityPath, targetSession, tenantPath, useTargets } from '../../identity/session';
import { fmtDate } from '../../identity/format';
import { useDesignWrite } from '../api/client';
import { CODE_RX, designTenants, normalizeCode, type DesignTenant } from '../api/tenants';
import { CodeField, designError, useEnableSwitch } from './Tenants';
import '../design.css';

/**
 * A scope that lets a tenant's users into Design's SPA: `design:warehousedesign:read|write` or a wildcard covering them
 * (`design:*` still counts, though nobody should be given it). The retired `design:layouts:*` grants nothing.
 */
const DESIGN_USER_SCOPE = /^design:(\*|warehousedesign:(\*|read|write))$/;

/**
 * One Design tenant: its registry row (edit, enable), its usage per collection and, for an operator who may read AUTH,
 * soft warnings about the AUTH tenant behind it (no Design scope, SSO on without SAML).
 */
export function DesignTenantDetailPage() {
  const { id = '' } = useParams();
  const { active: target } = useTargets();
  const canWrite = useDesignWrite();
  const authRead = hasScope(useHubLogin()?.scopes, SCOPES.authRead);
  const data = useAsync(async () => {
    const api = designTenants(target);
    const [tenant, usage] = await Promise.all([api.get(id), api.usage(id)]);
    return { tenant, usage };
  }, [target, id]);
  const authTenant = useAsync(async () => {
    if (!authRead || !targetSession(target).isSignedIn()) return null;
    const client = authClientFor(target);
    const [tenant, saml] = await Promise.all([
      client.tenants.get(id).catch(() => null),
      client.tenants.saml.get(id).catch(() => null),
    ]);
    return { tenant, saml };
  }, [target, id, authRead]);
  const { toggle, toast } = useEnableSwitch(target, () => void data.reload());
  const [editing, setEditing] = useState(false);

  const t = data.data?.tenant;
  const usage = data.data?.usage;
  const a = authTenant.data;
  const noScope =
    a?.tenant && !a.tenant.claims.some((c) => c.type === 'scope' && DESIGN_USER_SCOPE.test(c.value));
  const noSaml = t?.sso && a && !a.saml?.enabled;

  return (
    <Page
      title={t ? t.name : 'NYCE Design tenant'}
      description={t ? `Sign-in code ${t.code}` : undefined}
      actions={
        <>
          <Link
            className="nyk-ui-btn nyk-ui-btn-ghost nyk-ui-btn-sm"
            to={identityPath('/design/tenants', target)}
          >
            All tenants
          </Link>
          {canWrite && t && (
            <Button size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
        </>
      }
    >
      {toast.element}
      <ErrorBox error={data.error} />
      <Loading loading={data.loading && !data.data} />
      {noScope && (
        <Callout tone="warning" title="The AUTH tenant holds no NYCE Design scope">
          Its users cannot sign in to NYCE Design until the AUTH tenant has design:warehousedesign:* (preset
          “NYCE Design user” on the tenant’s claims; read-only users are narrowed by a role to
          design:warehousedesign:read).
        </Callout>
      )}
      {noSaml && (
        <Callout tone="warning" title="Single sign-on is on, but the AUTH tenant has no enabled SAML sign-in">
          Users get a Microsoft button that cannot work: set up single sign-on on the AUTH tenant or turn SSO
          off here.
        </Callout>
      )}
      {t && (
        <div className="nyk-card">
          <div className="nyk-card-head">
            <h2>Registry</h2>
          </div>
          <dl className="ds-facts">
            <dt>Code</dt>
            <dd className="nyk-mono">{t.code}</dd>
            <dt>Name</dt>
            <dd>{t.name}</dd>
            <dt>Enabled</dt>
            <dd>
              {canWrite ? (
                <Switch
                  checked={t.enabled}
                  label={t.enabled ? 'on' : 'off'}
                  onChange={(on) => void toggle(t, on)}
                />
              ) : (
                <Chip size="sm" className={t.enabled ? 'nyk-tone-green' : 'nyk-tone-grey'}>
                  {t.enabled ? 'enabled' : 'disabled'}
                </Chip>
              )}
            </dd>
            <dt>Single sign-on</dt>
            <dd>{t.sso ? 'on' : 'off'}</dd>
            <dt>AUTH tenant</dt>
            <dd>
              <Link to={tenantPath(t.id)} aria-label={`AUTH tenant ${a?.tenant?.name ?? t.id}`}>
                {a?.tenant?.name ?? <span className="nyk-mono">{t.id}</span>}
              </Link>
            </dd>
            <dt>Created</dt>
            <dd>
              {fmtDate(t.createdAt)}
              {t.createdBy && <span className="nyk-ui-hint"> · {t.createdBy}</span>}
            </dd>
            <dt>Updated</dt>
            <dd>
              {fmtDate(t.updatedAt)}
              {t.updatedBy && <span className="nyk-ui-hint"> · {t.updatedBy}</span>}
            </dd>
          </dl>
        </div>
      )}
      {usage && (
        <div className="nyk-card">
          <div className="nyk-card-head">
            <h2>Usage</h2>
          </div>
          <p className="nyk-card-intro" data-testid="design-usage-total">
            {usage.total.toLocaleString()} documents
            {usage.lastWriteAt ? `, last write ${fmtDate(usage.lastWriteAt)}` : ', never written'}
          </p>
          <TableScroller>
            <table className="nyk-ui-table" data-testid="design-usage" aria-label="Documents per collection">
              <thead>
                <tr>
                  <th>Collection</th>
                  <th className="nyk-ui-num">Documents</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(usage.collections)
                  .sort(([x], [y]) => x.localeCompare(y))
                  .map(([name, count]) => (
                    <tr key={name}>
                      <td className="nyk-mono">{name}</td>
                      <td className="nyk-ui-num">{count.toLocaleString()}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {editing && t && (
        <EditTenantDialog
          target={target}
          tenant={t}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setEditing(false);
            toast.success(`Saved ${next.name}`);
            void data.reload();
          }}
        />
      )}
    </Page>
  );
}

function EditTenantDialog({
  target,
  tenant,
  onClose,
  onSaved,
}: {
  target: string;
  tenant: DesignTenant;
  onClose: () => void;
  onSaved: (t: DesignTenant) => void;
}) {
  const [code, setCode] = useState(tenant.code);
  const [name, setName] = useState(tenant.name);
  const [sso, setSso] = useState(tenant.sso);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const codeChanged = normalizeCode(code) !== tenant.code;

  const submit = async () => {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      onSaved(
        await designTenants(target).update(tenant.id, {
          code: normalizeCode(code),
          name: name.trim(),
          enabled: tenant.enabled,
          sso,
        })
      );
    } catch (e) {
      const d = designError(e);
      if (d?.field) setFieldErrors({ [d.field]: d.error });
      else setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormDialog
      open
      title={`Edit ${tenant.name}`}
      size="sm"
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Save"
      canSubmit={CODE_RX.test(normalizeCode(code)) && name.trim().length > 0}
      busy={busy}
      error={error}
      draft={{ code, name, sso }}
      formClassName="ds-form"
    >
      <CodeField
        value={code}
        onChange={setCode}
        error={fieldErrors.code}
        hint={codeChanged ? `Users must type the new code instead of ${tenant.code}.` : undefined}
      />
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={fieldErrors.name}
      />
      <Switch checked={sso} onChange={setSso} label="Single sign-on (Microsoft)" />
    </FormDialog>
  );
}
